/**
 * AM-015 + AM-018 — interim hooks, hand-written in the exact shape orval
 * would generate against v1.ts once the backend patches are applied and
 * `orval` is re-run.
 *
 * IMPORTANT: this file is a STOPGAP. As soon as Antigravity applies the
 * backend patches and the OpenAPI schema regenerates, DELETE this file and
 * import the real generated hooks from v1.ts instead — do not maintain two
 * parallel copies of the same hooks long-term.
 *
 * Naming convention preserved from the real orval output (memory note):
 *   - Query hooks:    `export function use...`
 *   - Mutation hooks:  `export const use...`
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance as apiClient } from '@/api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HoldReasonCode {
  id: string;
  code: string;
  description: string;
  is_active: boolean;
}

export interface HoldProductionOrderBody {
  reason_code_id: string;
  notes?: string;
  downtime_record_id?: string | null;
}

export interface SwitchProductBody {
  replacement_production_order_id: string;
}

export interface PauseWorkOrderBody {
  hold_reason_id?: string | null;
  hold_notes?: string;
}

export interface StartWorkOrderBody {
  allow_concurrent?: boolean;
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useListHoldReasonCodes(activeOnly = true) {
  return useQuery({
    queryKey: ['hold-reason-codes', { activeOnly }],
    queryFn: async () => {
      const data = await apiClient.get<HoldReasonCode[] | { results: HoldReasonCode[] }>(
        '/api/v1/production/hold-reason-codes/',
        { params: activeOnly ? { is_active: true } : undefined }
      );
      const res = data.data;
      return Array.isArray(res) ? res : res.results ?? [];
    },
  });
}

export function useListProductionOrders(params?: { status?: string }) {
  return useQuery({
    queryKey: ['production-orders', params],
    queryFn: async () => {
      const data = await apiClient.get<any[] | { results: any[] }>(
        '/api/v1/production/production-orders/',
        { params }
      );
      const res = data.data;
      return Array.isArray(res) ? res : res.results ?? [];
    },
  });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useHoldProductionOrder = (productionOrderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: HoldProductionOrderBody) => {
      const { data } = await apiClient.post(
        `/api/v1/production/production-orders/${productionOrderId}/hold/`,
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-order', productionOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', { productionOrderId }] });
    },
  });
};

export const useResumeProductionOrder = (productionOrderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post(
        `/api/v1/production/production-orders/${productionOrderId}/resume/`
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-order', productionOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', { productionOrderId }] });
    },
  });
};

export const useSwitchProduct = (productionOrderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SwitchProductBody) => {
      const { data } = await apiClient.post(
        `/api/v1/production/production-orders/${productionOrderId}/switch-product/`,
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['production-order', productionOrderId] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
    },
  });
};

export const usePauseWorkOrder = (workOrderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: PauseWorkOrderBody = {}) => {
      const { data } = await apiClient.post(`/api/v1/production/work-orders/${workOrderId}/pause/`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
    },
  });
};

/**
 * AM-018 Rule WCO-01/WCO-02. Throws with a distinguishable shape so the UI
 * can offer "start anyway" — see StartWorkOrderButton.tsx for how the 400
 * response is caught and re-tried with allow_concurrent=true.
 */
export const useStartWorkOrder = (workOrderId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: StartWorkOrderBody = {}) => {
      const { data } = await apiClient.post(`/api/v1/production/work-orders/${workOrderId}/start/`, body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId] });
    },
  });
};
